import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Quote } from 'lucide-react';
import { testimonials } from '../data/mock';

const Testimonials = () => {
  const sectionRef = useRef(null);
  const [active, setActive] = useState(0);
  const [direction, setDirection] = useState(0); // -1 left, 1 right
  const [isAnimating, setIsAnimating] = useState(false);
  const total = testimonials.length;

  const goTo = useCallback((index, dir) => {
    if (isAnimating || index === active) return;
    setDirection(dir);
    setIsAnimating(true);
    setTimeout(() => {
      setActive(index);
      setIsAnimating(false);
    }, 300);
  }, [active, isAnimating]);

  const prev = useCallback(() => {
    const next = active === 0 ? total - 1 : active - 1;
    goTo(next, -1);
  }, [active, total, goTo]);

  const next = useCallback(() => {
    const nextIdx = active === total - 1 ? 0 : active + 1;
    goTo(nextIdx, 1);
  }, [active, total, goTo]);

  // Keyboard navigation
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [prev, next]);

  // Scroll reveal
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) entry.target.classList.add('visible'); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const t = testimonials[active];

  return (
    <section className="py-24 sm:py-32 bg-white">
      <div ref={sectionRef} className="reveal max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-14 sm:mb-16">
          <p className="text-sm font-semibold uppercase tracking-widest mb-3 font-heading" style={{ color: '#2693FF' }}>
            Client Voices
          </p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 font-heading mb-5">
            Trusted by engineering leaders
          </h2>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed">
            From CEOs to VPs of Engineering — hear from the leaders who chose Clik
            for their most critical initiatives.
          </p>
        </div>

        {/* Carousel */}
        <div className="relative">
          {/* Arrow buttons */}
          <button
            onClick={prev}
            className="absolute -left-3 sm:-left-5 lg:-left-14 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:border-slate-300 transition-all duration-200 cursor-pointer"
            style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
            aria-label="Previous testimonial"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={next}
            className="absolute -right-3 sm:-right-5 lg:-right-14 top-1/2 -translate-y-1/2 z-10 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:border-slate-300 transition-all duration-200 cursor-pointer"
            style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
            aria-label="Next testimonial"
          >
            <ChevronRight className="w-5 h-5" />
          </button>

          {/* Card */}
          <div
            className="rounded-2xl border border-slate-100 bg-white overflow-hidden"
            style={{ boxShadow: '0 4px 24px rgba(0,0,0,0.05)' }}
          >
            <div
              className="transition-all duration-300 ease-out"
              style={{
                opacity: isAnimating ? 0 : 1,
                transform: isAnimating
                  ? `translateX(${direction * 40}px)`
                  : 'translateX(0)',
              }}
            >
              <div className="flex flex-col md:flex-row">
                {/* Photo side */}
                <div
                  className="md:w-72 lg:w-80 flex-shrink-0 p-8 sm:p-10 flex flex-col items-center justify-center border-b md:border-b-0 md:border-r border-slate-100 cursor-pointer"
                  style={{ background: '#FAFBFC' }}
                  onClick={next}
                  title="Click for next"
                >
                  <img
                    src={t.avatar}
                    alt={`${t.author}, ${t.position} at ${t.company}`}
                    className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover mb-5"
                    style={{
                      boxShadow: '0 0 0 4px white, 0 0 0 6px rgba(38,147,255,0.15)',
                    }}
                    loading="lazy"
                  />
                  <div className="text-center">
                    <div className="font-semibold text-slate-900 text-base">{t.author}</div>
                    <div className="text-slate-500 text-sm mt-0.5">{t.position}</div>
                    <div className="text-slate-400 text-sm">{t.company}</div>
                  </div>
                  {t.companyLogo && (
                    <img
                      src={t.companyLogo}
                      alt={`${t.company} logo`}
                      className="h-6 mt-5 opacity-40"
                      loading="lazy"
                    />
                  )}
                </div>

                {/* Quote side */}
                <div className="flex-1 p-8 sm:p-10 lg:p-12 flex flex-col justify-center">
                  <Quote
                    className="w-10 h-10 mb-5 flex-shrink-0"
                    style={{ color: 'rgba(38,147,255,0.12)' }}
                  />
                  <p className="text-slate-700 leading-relaxed text-base sm:text-lg mb-6">
                    "{t.quote}"
                  </p>
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <span className="w-8 h-px bg-slate-300" />
                    {t.author}, {t.position} at {t.company}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Dot indicators */}
          <div className="flex items-center justify-center gap-2 mt-8">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i, i > active ? 1 : -1)}
                className="cursor-pointer transition-all duration-300"
                style={{
                  width: i === active ? '24px' : '8px',
                  height: '8px',
                  borderRadius: '4px',
                  background: i === active ? '#2693FF' : '#cbd5e1',
                }}
                aria-label={`Go to testimonial ${i + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
